function is_marketplace_realtime_payload(payload = {}, event_type = "") {
    return payload?.module === "marketplace" && payload?.type === event_type;
}

function normalize_id(value) {
    if (value === null || value === undefined) return "";
    return String(value);
}

function get_listing_from_realtime_payload(data = {}) {
    if (data?.listing?.id) return data.listing;

    return {
        id: data?.product_id,
        is_published: data?.is_published,
        product_type: {
            name: data?.listing_type,
        },
    };
}

function get_listing_base_profile_ids(listing = {}) {
    return [
        listing?.base_profile_id,
        listing?.base_profile?.id,
        listing?.base_profile?.company?.base_profile?.id,
    ]
        .filter((id) => id !== undefined && id !== null)
        .map(String);
}

function upsert_item_in_list(list = [], item = {}) {
    const item_id = normalize_id(item?.id);
    const next_list = Array.isArray(list) ? Array.from(list) : [];
    const existing_index = next_list.findIndex((existing) => normalize_id(existing?.id) === item_id);

    if (existing_index >= 0) {
        const merged_item = {
            ...Reflect.get(next_list, existing_index),
            ...item,
        };
        const deduped_list = next_list.filter((existing) => normalize_id(existing?.id) !== item_id);
        deduped_list.splice(existing_index, 0, merged_item);

        return {
            next_list: deduped_list,
            did_insert: false,
        };
    }

    next_list.push(item);

    return {
        next_list,
        did_insert: existing_index < 0,
    };
}

export const marketplace_listing_realtime_methods = {
    handle_marketplace_realtime_event(event) {
        const payload = event?.detail || {};
        if (!is_marketplace_realtime_payload(payload, "marketplace_listing_changed")) {
            return;
        }

        this.cancel_marketplace_auto_paginate();
        this.apply_marketplace_listing_realtime_change(payload?.data || {});
    },
    cancel_marketplace_auto_paginate() {
        this.auto_paginate_token += 1;
        if (this.auto_paginate_timeout_id) {
            clearTimeout(this.auto_paginate_timeout_id);
            this.auto_paginate_timeout_id = null;
        }
    },
    is_current_marketplace_request(request_token) {
        return request_token === this.auto_paginate_token;
    },
    async apply_marketplace_listing_realtime_change(data = {}) {
        let listing = data?.listing;
        const listing_id = normalize_id(data?.product_id || listing?.id);
        if (!listing_id) return;

        if (this.marketplace_listing_should_be_removed(data, listing)) {
            this.remove_marketplace_listing_from_lists(listing_id);
            return;
        }

        if (!listing?.id) {
            listing = await this.fetch_marketplace_listing_for_realtime(listing_id);
        }

        const listing_type = normalize_id(data?.listing_type || listing?.product_type?.name).trim().toLowerCase();
        const list_name = listing_type === "service" ? "services" : "products";

        if (!listing?.id || !this.marketplace_listing_matches_current_view(listing, listing_type)) {
            return;
        }

        this.upsert_marketplace_listing(list_name, listing);
        this.upsert_marketplace_listing("supplier_source_products", listing, false);
        this.refresh_infinite_scroll();
    },
    marketplace_listing_should_be_removed(data = {}, listing = {}) {
        return data?.action === "deleted" || data?.is_published === false || listing?.is_published === false;
    },
    async fetch_marketplace_listing_for_realtime(product_id) {
        try {
            return await this.fetch_marketplace_product_details(product_id);
        } catch (error) {
            console.error("Failed to hydrate marketplace realtime listing:", error);
            return null;
        }
    },
    remove_marketplace_listing_from_lists(listing_id) {
        this.products = this.remove_marketplace_listing_from_list(this.products, listing_id);
        this.services = this.remove_marketplace_listing_from_list(this.services, listing_id);
    },
    remove_marketplace_listing_from_list(list = [], listing_id) {
        if (!Array.isArray(list)) return list;
        return list.filter((item) => normalize_id(item?.id) !== normalize_id(listing_id));
    },
    marketplace_listing_matches_current_view(listing = {}, listing_type = "") {
        if (this.showing_wishlist) return false;

        const normalized_listing_type = normalize_id(listing_type).trim().toLowerCase();
        if (this.active_listing_type === "services" && normalized_listing_type !== "service") return false;
        if (this.active_listing_type !== "services" && normalized_listing_type === "service") return false;

        return this.marketplace_listing_matches_local_filters(listing);
    },
    marketplace_listing_matches_local_filters(listing = {}) {
        if (!this.filter_products_by_supplier([listing]).length) return false;
        if (this.active_listing_type !== "services" && !this.filter_products_by_category([listing]).length) return false;
        if (!this.marketplace_listing_matches_search_filter(listing)) return false;

        return true;
    },
    marketplace_listing_matches_search_filter(listing = {}) {
        const query = normalize_id(this.filters.search).trim().toLowerCase();
        if (!query) return true;

        return [
            listing?.name,
            listing?.description,
            listing?.category,
            listing?.subcategory,
            this.get_company_name(listing),
        ].some((value) => normalize_id(value).toLowerCase().includes(query));
    },
    upsert_marketplace_listing(list_name, listing, should_increment_count = true) {
        const { next_list, did_insert } = upsert_item_in_list(Reflect.get(this, list_name), listing);

        if (did_insert && should_increment_count) {
            this.increment_marketplace_listing_count(list_name);
        }

        Reflect.set(this, list_name, next_list);
    },
    increment_marketplace_listing_count(list_name) {
        const pagination_key = list_name === "services" ? "pagination_services" : "pagination_products";
        const pagination = Reflect.get(this, pagination_key) || {};

        Reflect.set(this, pagination_key, {
            ...pagination,
            total_count: Number(pagination.total_count || 0) + 1,
        });
    },
};

function create_marketplace_realtime_event_handler(include_orders) {
    return function handle_marketplace_realtime_event(event) {
        if (include_orders) {
            this.handle_marketplace_order_realtime_event(event);
        }
        this.handle_marketplace_listing_realtime_event(event);
    };
}

function create_marketplace_order_realtime_handler(include_orders) {
    return function handle_marketplace_order_realtime_event(event) {
        if (!include_orders) return;

        const payload = event?.detail || {};
        if (!is_marketplace_realtime_payload(payload, "marketplace_order_changed")) {
            return;
        }

        if (!this.marketplace_order_event_targets_shop(payload?.data || {})) {
            return;
        }

        this.upsert_marketplace_order_from_realtime(payload.data);
    };
}

function marketplace_order_event_targets_shop(data = {}) {
    const seller_base_profile_ids = (data.seller_base_profile_ids || []).map(String);
    return this.base_profile_id && seller_base_profile_ids.includes(String(this.base_profile_id));
}

function upsert_marketplace_order_from_realtime(data = {}) {
    const seller_order = Reflect.get(data?.seller_orders || {}, String(this.base_profile_id));
    if (!seller_order?.id) return;

    const { next_list } = upsert_item_in_list(this.orders, seller_order);
    this.orders = next_list;
}

function handle_marketplace_listing_realtime_event(event) {
    const payload = event?.detail || {};
    if (!is_marketplace_realtime_payload(payload, "marketplace_listing_changed")) {
        return;
    }

    const listing = get_listing_from_realtime_payload(payload?.data || {});
    if (!listing?.id || !this.marketplace_listing_event_targets_catalogue(listing)) {
        return;
    }

    this.upsert_catalogue_listing_from_realtime(listing);
}

function create_catalogue_listing_target_checker(listing_type) {
    return function marketplace_listing_event_targets_catalogue(listing = {}) {
        const current_base_profile_id = normalize_id(this.base_profile_id);
        const current_listing_type = normalize_id(listing?.product_type?.name).trim().toLowerCase();
        const listing_base_profile_ids = get_listing_base_profile_ids(listing);
        const matches_existing_catalogue_item = this.profile_products.some(
            (item) => normalize_id(item?.id) === normalize_id(listing?.id)
        );
        const matches_listing_type = listing_type === "service"
            ? current_listing_type === "service"
            : this.is_shop_product(listing);

        return Boolean(
            (
                matches_existing_catalogue_item
                || (
                    current_base_profile_id
                    && listing_base_profile_ids.includes(current_base_profile_id)
                )
            )
            && matches_listing_type
        );
    };
}

function upsert_catalogue_listing_from_realtime(listing) {
    const { next_list } = upsert_item_in_list(this.profile_products, listing);

    this.sync_catalogue_items(next_list);
    if (normalize_id(this.modal_product?.id) === normalize_id(listing.id)) {
        this.modal_product = {
            ...this.modal_product,
            ...listing,
        };
    }
}

export function create_marketplace_catalogue_realtime_methods(options = {}) {
    const listing_type = options.listing_type || "product";
    const include_orders = options.include_orders === true;

    return {
        handle_marketplace_realtime_event: create_marketplace_realtime_event_handler(include_orders),
        handle_marketplace_order_realtime_event: create_marketplace_order_realtime_handler(include_orders),
        marketplace_order_event_targets_shop,
        upsert_marketplace_order_from_realtime,
        handle_marketplace_listing_realtime_event,
        marketplace_listing_event_targets_catalogue: create_catalogue_listing_target_checker(listing_type),
        upsert_catalogue_listing_from_realtime,
    };
}
